import { sourcingApi } from '@/api';
import { queryKeys } from '@/api/query-keys';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  AddLinesRequest,
  AddParticipantsRequest,
  AwardTenderRequest,
  CreateTenderRequest,
  ListTendersFilters,
  SubmitOfferRequest,
  UpdateLineRequest,
  UpdateTenderMetaRequest,
} from './sourcing.types';

// --- Queries -------------------------------------------------------------

export const useTenders = (filters?: ListTendersFilters) =>
  useQuery({
    queryKey: queryKeys.sourcing.list(filters),
    queryFn: () => sourcingApi.list(filters),
    placeholderData: keepPreviousData,
  });

export const useTenderDetail = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.sourcing.detail(id ?? ''),
    queryFn: () => sourcingApi.detail(id!),
    enabled: !!id,
  });

export const useTenderOffers = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.sourcing.offers(id ?? ''),
    queryFn: () => sourcingApi.offers(id!),
    enabled: !!id,
  });

export const useTenderNotifications = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.sourcing.notifications(id ?? ''),
    queryFn: () => sourcingApi.notifications(id!),
    enabled: !!id,
  });

export const useItemCandidates = (q: string) =>
  useQuery({
    queryKey: queryKeys.sourcing.itemCandidates(q),
    queryFn: () => sourcingApi.itemCandidates(q),
    enabled: q.trim().length >= 2,
  });

export const useTenderStatusCounts = () =>
  useQuery({
    queryKey: queryKeys.sourcing.statusCounts(),
    queryFn: () => sourcingApi.statusCounts(),
  });

export const useItemBulkLookup = () =>
  useMutation({
    mutationFn: (tokens: string[]) => sourcingApi.itemBulkLookup(tokens),
  });

export const useSupplierCandidates = (q: string) =>
  useQuery({
    queryKey: queryKeys.sourcing.supplierCandidates(q),
    queryFn: () => sourcingApi.supplierCandidates(q),
    enabled: q.trim().length >= 2,
  });

export const useSupplierContacts = (supplierId: number | undefined) =>
  useQuery({
    queryKey: queryKeys.sourcing.supplierContacts(supplierId ?? 0),
    queryFn: () => sourcingApi.supplierContacts(supplierId!),
    enabled: !!supplierId,
  });

export const usePortalView = (token: string | undefined) =>
  useQuery({
    queryKey: queryKeys.sourcing.portal(token ?? ''),
    queryFn: () => sourcingApi.portalView(token!),
    enabled: !!token,
  });

// --- Mutations -----------------------------------------------------------

const useSourcingInvalidation = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.sourcing.all });
};

export const useCreateTender = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: (body: CreateTenderRequest) => sourcingApi.create(body),
    onSuccess: invalidate,
  });
};

export const useUpdateTenderMeta = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdateTenderMetaRequest;
    }) => sourcingApi.updateMeta(id, body),
    onSuccess: invalidate,
  });
};

export const usePublishTender = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: (id: string) => sourcingApi.publish(id),
    onSuccess: invalidate,
  });
};

export const useCloseTender = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: (id: string) => sourcingApi.close(id),
    onSuccess: invalidate,
  });
};

export const useCancelTender = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: (id: string) => sourcingApi.cancel(id),
    onSuccess: invalidate,
  });
};

export const useAwardTender = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AwardTenderRequest }) =>
      sourcingApi.award(id, body),
    onSuccess: invalidate,
  });
};

export const useAddLines = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AddLinesRequest }) =>
      sourcingApi.addLines(id, body),
    onSuccess: invalidate,
  });
};

export const useUpdateLine = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({
      id,
      lineId,
      body,
    }: {
      id: string;
      lineId: string;
      body: UpdateLineRequest;
    }) => sourcingApi.updateLine(id, lineId, body),
    onSuccess: invalidate,
  });
};

export const useRemoveLine = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({ id, lineId }: { id: string; lineId: string }) =>
      sourcingApi.removeLine(id, lineId),
    onSuccess: invalidate,
  });
};

export const useBulkDeleteLines = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({ id, lineIds }: { id: string; lineIds: string[] }) =>
      sourcingApi.bulkDeleteLines(id, lineIds),
    onSuccess: invalidate,
  });
};

export const useDuplicateTender = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: (id: string) => sourcingApi.duplicate(id),
    onSuccess: invalidate,
  });
};

export const useResendInvitation = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({
      id,
      participantId,
    }: {
      id: string;
      participantId: string;
    }) => sourcingApi.resendInvitation(id, participantId),
    onSuccess: invalidate,
  });
};

export const useAddParticipants = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: AddParticipantsRequest;
    }) => sourcingApi.addParticipants(id, body),
    onSuccess: invalidate,
  });
};

export const useRemoveParticipant = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({
      id,
      participantId,
    }: {
      id: string;
      participantId: string;
    }) => sourcingApi.removeParticipant(id, participantId),
    onSuccess: invalidate,
  });
};

export const useLinkContactList = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({ id, listId }: { id: string; listId: number }) =>
      sourcingApi.linkContactList(id, listId),
    onSuccess: invalidate,
  });
};

export const useUnlinkContactList = () => {
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({ id, listId }: { id: string; listId: number }) =>
      sourcingApi.unlinkContactList(id, listId),
    onSuccess: invalidate,
  });
};

export const useSubmitOffer = (token: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitOfferRequest) =>
      sourcingApi.submitOffer(token, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.sourcing.portal(token) }),
  });
};
